import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import { FamilyStatus } from "protogen/advisors_service_pb";
import {
  isDigitalDeactivatedStatus,
  memberSelfServeReactivationPath,
} from "utils/memberFamilyPlan";
import NoticeBanner from "components/common/NoticeBanner";
import { DEACTIVATION_GRACE_PERIOD_DAYS } from "common/constants";
import { SxProps } from "@mui/system";
import useIsMobile from "components/hooks/useIsMobile";

function getDaysUntilAccessEnds(endDate: string | undefined): number | null {
  if (!endDate) return null;
  const end = new Date(endDate);
  const gracePeriodEnd = new Date(end);
  gracePeriodEnd.setDate(
    gracePeriodEnd.getDate() + DEACTIVATION_GRACE_PERIOD_DAYS,
  );
  const now = new Date();
  const daysLeft = Math.ceil(
    (gracePeriodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );
  return daysLeft > 0 ? daysLeft : 0;
}

export default ({ sx }: { sx?: SxProps }) => {
  const navigate = useNavigate();
  const { data } = useMemberDetails();
  const isMobile = useIsMobile();
  if (!data?.family) return null;

  const familyStatus = data.family.status;
  const isDigitalDeactivated = isDigitalDeactivatedStatus(familyStatus);
  const isDeactivated =
    familyStatus === FamilyStatus.DEACTIVATED ||
    isDigitalDeactivated ||
    familyStatus === FamilyStatus.DEACTIVATED_PROSPECT;

  if (!isDeactivated) return null;

  const daysLeft = getDaysUntilAccessEnds(data.family.endDate);
  if (daysLeft === null || daysLeft <= 0) return null;

  return (
    <Box
      sx={{
        position: "relative",
        ...(isMobile && {
          zIndex: 1201,
          backgroundColor: "#FFF",
        }),
      }}
    >
      <NoticeBanner
        eyebrowText="Deactivated Access"
        title="You have read-only access to your tasks, documents, and secrets."
        subtext={`Access to deactivated content is only temporary. We will lock down that data in ${daysLeft} ${
          daysLeft === 1 ? "day" : "days"
        }`}
        buttonText={
          isDigitalDeactivated ? "Upgrade plan" : "Re-activate service"
        }
        buttonVariant="contained"
        onClick={() => navigate(memberSelfServeReactivationPath(familyStatus))}
        sx={sx}
      />
    </Box>
  );
};
